import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiStep from '@mui/material/Step';
import MuiStepLabel from '@mui/material/StepLabel';
import MuiStepper from '@mui/material/Stepper';
import * as React from 'react';
import { FlexBox } from '../box';
import { IconButton } from '../button';
import { usePaletteColor, useRubiksTheme } from '../theme';
import { firstLetterUppercase, getSxLineClamp } from '../typography';
export const Stepper = (props) => {
    const { activeStep, steps } = props;
    const { theme: { spacing } } = useRubiksTheme();
    const [currentStep, setCurrentStep] = React.useState(activeStep);
    const completedColor = usePaletteColor('components.stepper.done');
    const defaultColor = usePaletteColor('components.stepper.default');
    const connectorColor = usePaletteColor('components.stepper.connector');
    const labelColor = usePaletteColor('text.primary');
    const labelCompletedColor = usePaletteColor('text.disabled');
    const primaryColor = usePaletteColor('primary');
    const backgroundColor = usePaletteColor('background.paper');
    React.useEffect(() => {
        setCurrentStep(activeStep);
    }, [activeStep]);
    const start = Math.max(Math.min(currentStep - 3, steps.length - 5), 0);
    const end = Math.min(start + 5, steps.length);
    const scrollLeft = start > 0;
    const scrollRight = end !== steps.length;
    const scrollEnabled = steps.length > 5;
    return (_jsxs(FlexBox, { stretch: true, children: [scrollEnabled && (_jsx(StepScroll, { direction: "left", disabled: !scrollLeft, onClick: () => setCurrentStep((step) => Math.min(step - 1, step - (step % 5))) })), _jsx(MuiStepper, { alternativeLabel: true, sx: {
                    flex: 1,
                    justifyContent: 'space-between',
                    overflow: 'hidden'
                }, children: steps.slice(start, end).map((step) => {
                    const index = steps.indexOf(step);
                    return (_jsx(MuiStep, { active: activeStep === index + 1, completed: activeStep > index + 1, index: index, sx: {
                            zIndex: steps.length - index,
                            display: 'flex',
                            justifyContent: 'center',
                            flexGrow: 0,
                            backgroundColor,
                            maxWidth: `calc(100% / ${Math.min(steps.length, 5)})`,
                            ':first-of-type': {
                                justifyContent: 'flex-start'
                            },
                            ':last-of-type': {
                                justifyContent: 'flex-end'
                            },
                            ':not(:last-child):after': {
                                content: '""',
                                position: 'absolute',
                                top: spacing(1),
                                left: `calc(50% + ${spacing(1)})`,
                                width: '50%',
                                height: '1px',
                                background: connectorColor
                            },
                            ':first-of-type:before': scrollLeft
                                ? {
                                    content: '""',
                                    pointerEvents: 'none',
                                    position: 'absolute',
                                    background: `linear-gradient(to right, ${connectorColor}00 40%, #00000010 100%)`,
                                    top: spacing(1),
                                    height: '1px',
                                    right: `calc(50% + ${spacing(1)})`,
                                    left: `calc(-50% + ${spacing(1)})`
                                }
                                : {},
                            ':last-of-type:before': scrollRight
                                ? {
                                    content: '""',
                                    pointerEvents: 'none',
                                    position: 'absolute',
                                    background: `linear-gradient(to left, ${connectorColor}00 40%, #00000010 100%)`,
                                    top: spacing(1),
                                    height: '1px',
                                    right: `calc(-50% + ${spacing(1)})`,
                                    left: `calc(50% + ${spacing(1)})`
                                }
                                : {},
                            ':first-of-type .MuiStepConnector-root': {
                                display: 'none'
                            },
                            '.MuiStepConnector-root': {
                                top: spacing(1),
                                left: '-100vw',
                                right: `calc(50% + ${spacing(1)})`,
                                '.MuiStepConnector-line': {
                                    borderColor: connectorColor
                                }
                            },
                            '.MuiSvgIcon-root': {
                                fontSize: '1rem',
                                color: defaultColor,
                                '&.Mui-active': {
                                    color: primaryColor
                                },
                                '&.Mui-completed': {
                                    color: completedColor
                                }
                            }
                        }, children: _jsx(MuiStepLabel, { sx: {
                                width: 120,
                                '.MuiStepLabel-alternativeLabel .MuiStepLabel-label': {
                                    mt: 1,
                                    userSelect: 'none',
                                    color: labelColor,
                                    fontSize: '0.875rem',
                                    fontWeight: 'normal',
                                    ...getSxLineClamp(2),
                                    '&.Mui-active': {
                                        fontWeight: 'bold'
                                    },
                                    '&.Mui-completed': {
                                        color: labelCompletedColor
                                    },
                                    ...firstLetterUppercase
                                }
                            }, children: step.label }) }, index));
                }) }), scrollEnabled && (_jsx(StepScroll, { direction: "right", disabled: !scrollRight, onClick: () => setCurrentStep((step) => Math.max(step + 1, 4)) }))] }));
};
const StepScroll = (props) => {
    const color = usePaletteColor('components.stepper.swipe');
    const icon = (() => {
        switch (props.direction) {
            case 'left':
                return 'IconChevronLeft';
            case 'right':
                return 'IconChevronRight';
        }
    })();
    return (_jsx(IconButton, { icon: icon, size: "small", sx: {
            width: 24,
            height: 24,
            flexBasis: 24,
            flexShrink: 0,
            flexGrow: 0,
            color,
            transform: 'translateY(-4px)',
            visibility: props.disabled ? 'hidden' : 'visible'
        }, onClick: props.onClick }));
};
